<template>
  <Modal
    width="1000"
    title="炼丹炉"
    v-model="alchemyVisible"
    :styles="{ top: '80px' }"
    class="alchemy-modal"
    footer-hide
    @on-visible-change="handleAlchemyModalVisible"
  >
    <div class="alchemy-layout">
      <!-- 左侧炼丹炉主区域 (70%) -->
      <div class="alchemy-furnace-area">
        <!-- 炼丹炉图片/动画区 -->
        <div class="furnace-visual" :class="{ 'refining': isRefining }">
          <div class="furnace-container">
            <!-- 炼丹炉图片 -->
            <img
              src="@/assets/liandanlu.png"
              alt="炼丹炉"
              class="furnace-img"
              v-if="!isRefining"
            >
            <!-- 炼丹动画 -->
            <div class="refining-animation" v-else>
              <div class="flame-effect"></div>
              <div class="smoke-effect"></div>
              <Icon type="ios-flame" size="64" color="#ff6b6b" class="flame-icon" />
              <p class="refining-text">炼丹中...</p>
              <Progress
                :percent="refiningProgress"
                :stroke-width="8"
                status="active"
                class="refining-progress"
              />
            </div>

            <!-- 选中丹药悬浮显示 -->
            <div class="selected-pill-float" v-if="selectedDisplayRecipe && !isRefining">
<!--              <img :src="selectedRecipe.itemIcon || '/static/images/pill-default.png'" class="pill-preview-img">-->
              <div class="pill-float-info">
                <div class="pill-name">{{ selectedDisplayRecipe.itemName }}</div>
                <div class="pill-grade" :class="selectedDisplayRecipe.grade">{{ selectedDisplayRecipe.gradeName }}</div>
              </div>
            </div>
          </div>
        </div>
        <!-- 图纸描述卡片 -->
        <div class="description-section" v-if="selectedRecipe && !selectedDelegatingItem">
          <div class="section-title">
            <Icon type="ios-document" />
            <span>丹方描述</span>
          </div>
          <div class="description-content">
            {{ selectedRecipe.desci || '暂无描述' }}
          </div>
        </div>
        <div class="description-section" v-if="selectedDelegatingItem">
          <div class="section-title">
            <Icon type="ios-list-box" />
            <span>炉内丹药准备情况</span>
          </div>
          <div class="description-content delegating-result-content">
            <div class="delegating-summary-row">
              <span>成功 {{ selectedDelegatingItem.successCount || 0 }} 颗</span>
              <span>失败 {{ selectedDelegatingItem.failCount || 0 }} 颗</span>
              <span v-if="isAlchemyProcessing(selectedDelegatingItem)">剩余 {{ getCountdown(selectedDelegatingItem.linTime) }}</span>
              <span v-else-if="isAlchemyReceivable(selectedDelegatingItem)">已可领取</span>
            </div>
            <div class="delegating-result-group" v-if="selectedDelegatingSuccessItems.length > 0">
              <div class="delegating-result-title">成功丹药</div>
              <div class="delegating-result-list">
                <span
                  v-for="(item, idx) in selectedDelegatingSuccessItems"
                  :key="'success-item-' + idx"
                  class="delegating-result-chip success"
                >
                  {{ item.displayName || item.itemName }}*{{ item.count }}
                </span>
              </div>
            </div>
            <div class="delegating-result-group" v-if="selectedDelegatingFailItems.length > 0">
              <div class="delegating-result-title">失败丹药</div>
              <div class="delegating-result-list">
                <span
                  v-for="(item, idx) in selectedDelegatingFailItems"
                  :key="'fail-item-' + idx"
                  class="delegating-result-chip fail"
                >
                  {{ item.displayName || item.itemName }}*{{ item.count }}
                </span>
              </div>
            </div>
            <div v-if="selectedDelegatingSuccessItems.length === 0 && selectedDelegatingFailItems.length === 0" class="delegating-result-empty">
              暂无炼制明细
            </div>
          </div>
        </div>
        <!-- 材料需求区 -->
        <div class="materials-section" v-if="selectedRecipe && !selectedDelegatingItem">
          <div class="section-title">
            <Icon type="ios-flask" />
            <span>所需材料</span>
            <span class="material-status" :class="{ 'ready': selectedRecipe.isMeet }">
              <Icon :type="selectedRecipe.isMeet ? 'ios-checkmark-circle' : 'ios-close-circle'" />
              {{ selectedRecipe.isMeet ? '材料齐全' : '材料不足' }}
            </span>
          </div>

          <div class="materials-grid">
            <div
              v-for="(mat, index) in selectedRecipe.matList"
              :key="index"
              class="material-slot"
              :class="{
                'sufficient': isMaterialEnough(mat, index),
                'insufficient': !isMaterialEnough(mat, index),
                'main-material': isMainMaterial(mat)
              }"
            >
              <div class="mat-icon">
                {{ mat.itemName }}
                <div class="mat-quantity-badge">
                  {{ mat.haveNum }}/{{ getRequiredTotal(mat, index) }}
                </div>
              </div>
              <div class="mat-info">
<!--                <div class="mat-name">{{ mat.itemName }}</div>-->
                <div class="mat-type">{{ isMainMaterial(mat) ? '主材' : '辅材' }}<span v-if="mat.crossInventory" class="material-source"> · 本体+灵田（{{ mat.sourcePolicy === 'BAG_FIRST' ? '优先本体' : (mat.sourcePolicy === 'FARM_FIRST' ? '优先灵田' : '合并' ) }}）</span></div>
              </div>

              <!-- 辅材数量调节 -->
              <div class="mat-adjust" v-if="!isMainMaterial(mat) && mat.haveNum > 0">
                <InputNumber
                  v-model="auxiliaryNums[index]"
                  :min="0"
                  :max="mat.haveNum"
                  :step="1"
                  size="small"
                  @on-change="(val) => onAuxiliaryChange(mat.itemId, val, index)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 炼丹信息区 -->
        <div class="alchemy-info-section" v-if="selectedRecipe && !selectedDelegatingItem">
          <!-- 成品预览 -->
          <div class="products-preview">
            <div class="product-box success-product">
              <div class="product-label">
                <Icon type="ios-checkmark-circle" color="#19be6b" />
                炼成
              </div>
              <div class="product-item">
                <img src="@/assets/danyao-opt.png" class="product-img" loading="lazy" decoding="async">
                <div class="product-name">{{ selectedRecipe.productName }}</div>
                <div class="product-count">x{{ selectedRecipe.productCount }}</div>
              </div>
            </div>

            <div class="product-box fail-product" v-if="selectedRecipe.failProductName">
              <div class="product-label">
                <Icon type="ios-close-circle" color="#ed4014" />
                失败
              </div>
              <div class="product-item">
                <img src="@/assets/yaozhai-opt.jpg" class="product-img" loading="lazy" decoding="async">
                <div class="product-name">{{ selectedRecipe.failProductName }}</div>
                <div class="product-count">x{{ selectedRecipe.failProductCount }}</div>
              </div>
            </div>
          </div>

          <div class="info-row">
            <!-- 成功率 -->
            <div class="info-block success-rate-block">
              <div class="block-label">
                <Icon type="ios-pulse" />
                成功率
              </div>
              <div class="block-value">
                <div class="rate-number" :class="getRateClass(selectedRecipe.totalSuccessRate)">
                  {{ (selectedRecipe.totalSuccessRate * 100).toFixed(1) }}%
                </div>
                <div class="rate-detail" v-if="showRateDetail">
                  <span>基础 {{ (selectedRecipe.basicRate * 100).toFixed(1) }}%</span>
                  <span class="plus" v-if="selectedRecipe.slotBonus > 0">+</span>
                  <span class="bonus" v-if="selectedRecipe.slotBonus > 0">时辰 {{ (selectedRecipe.slotBonus * 100).toFixed(1) }}%</span>
                  <span class="plus" v-if="selectedRecipe.caiRate > 0">+</span>
                  <span class="bonus" v-if="selectedRecipe.caiRate > 0">辅材 {{ (selectedRecipe.caiRate * 100).toFixed(1) }}%</span>
                  <span class="plus" v-if="selectedRecipe.proficiencyBonus > 0">+</span>
                  <span class="bonus" v-if="selectedRecipe.proficiencyBonus > 0">熟练 {{ (selectedRecipe.proficiencyBonus * 100).toFixed(1) }}%</span>
                </div>
              </div>
            </div>

            <!-- 熟练度 -->
            <div class="info-block proficiency-block">
              <div class="block-label">
                <Icon type="ios-school" />
                熟练度
              </div>
              <div class="block-value">
                <div class="proficiency-level">Lv.{{ selectedRecipe.proficiencyLevel }}</div>
                <Progress
                  :percent="proficiencyPercent"
                  :stroke-width="6"
                  hide-info
                  class="proficiency-bar"
                />
                <div class="proficiency-text">
                  {{ selectedRecipe.currentProficiency }}/{{ selectedRecipe.maxProficiency }}
                </div>
              </div>
            </div>

            <!-- 耗时与消耗 -->
            <div class="info-block cost-block">
              <div class="block-label">
                <Icon type="ios-time" />
                耗时/消耗
              </div>
              <div >
                <div class="cost-item">
                  <Icon type="ios-clock-outline" />
                  <span>{{ selectedRecipe.costTime }}s</span>
                </div>
                <div class="cost-item">
                  <Icon type="ios-flash" />
                  <span>
                    {{ Number(selectedRecipe.costCoin || 0) }}灵石
                  </span>
                </div>
              </div>
            </div>

            <!-- 炼丹按钮（与成功率/熟练度/耗时同一行） -->
            <div class="info-block action-inline-block">
              <div class="block-label">
                <Icon type="ios-flame" />
                炼丹
              </div>
              <div class="block-value action-inline-content">
                <div class="batch-tip">当前为一炉{{ selectedRecipe.batchMax || 12 }}颗</div>
                <Button
                  type="primary"
                  size="large"
                  :disabled="!canRefine"
                  :loading="isRefining"
                  @click="startRefining"
                  class="refine-btn"
                  :class="{ 'can-refine': canRefine }"
                >
                  <Icon :type="isRefining ? 'ios-loading' : 'ios-flame'" size="20" />
                  {{ isRefining ? '炼丹中...' : '开始炼丹' }}
                </Button>
                <div class="cannot-refine-reason" v-if="!canRefine">
                  {{ cannotRefineReason }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="alchemy-info-section" v-if="selectedDelegatingItem">
          <div class="info-row">
            <div class="info-block success-rate-block">
              <div class="block-label">
                <Icon type="ios-pulse" />
                成功率
              </div>
              <div class="block-value">
                <div class="rate-number" :class="getRateClass(Number(selectedDelegatingItem.rate || 0))">
                  {{ (Number(selectedDelegatingItem.rate || 0) * 100).toFixed(1) }}%
                </div>
              </div>
            </div>
            <div class="info-block proficiency-block">
              <div class="block-label">
                <Icon type="ios-checkmark-circle" />
                炉次结果
              </div>
              <div class="block-value">
                <div class="proficiency-level">{{ selectedDelegatingItem.name || '炼丹委托' }}</div>
                <div class="proficiency-text">完成时间 {{ selectedDelegatingItem.linTime || '--' }}</div>
              </div>
            </div>
            <div class="info-block cost-block">
              <div class="block-label">
                <Icon type="ios-time" />
                当前状态
              </div>
              <div>
                <div class="cost-item" v-if="isAlchemyProcessing(selectedDelegatingItem)">
                  <Icon type="ios-time-outline" />
                  <span>炼制中 {{ getCountdown(selectedDelegatingItem.linTime) }}</span>
                </div>
                <div class="cost-item" v-else-if="isAlchemyReceivable(selectedDelegatingItem)">
                  <Icon type="ios-checkmark-circle-outline" />
                  <span>已完成，可回收</span>
                </div>
                <div class="cost-item" v-else>
                  <Icon type="ios-archive-outline" />
                  <span>已领取</span>
                </div>
              </div>
            </div>
            <div class="info-block action-inline-block">
              <div class="block-label">
                <Icon type="ios-flame" />
                回收
              </div>
              <div class="block-value action-inline-content">
                <div class="batch-tip">时间到了后才能回收丹药</div>
                <Button
                  v-if="isAlchemyReceivable(selectedDelegatingItem)"
                  type="primary"
                  size="large"
                  :loading="receivingIndex === selectedDelegatingIndex"
                  @click="receiveAlchemy(selectedDelegatingItem, selectedDelegatingIndex)"
                  class="refine-btn can-refine"
                >
                  <Icon type="ios-checkmark-circle" size="20" />
                  回收丹药
                </Button>
                <Button
                  v-else
                  type="default"
                  size="large"
                  disabled
                  class="refine-btn"
                >
                  <Icon type="ios-time" size="20" />
                  {{ isAlchemyProcessing(selectedDelegatingItem) ? '炼制中...' : '已领取' }}
                </Button>
              </div>
            </div>
          </div>
        </div>



        <!-- 未选择图纸提示 -->
        <div class="no-recipe-tip" v-if="!selectedRecipe && !selectedDelegatingItem && !isRefining">
          <Icon type="ios-flask-outline" size="64" color="#999" />
          <p>从右侧选择丹方开始炼丹</p>
        </div>
      </div>

      <!-- 右侧图纸列表 (30%) -->
      <div class="alchemy-recipe-list">
        <div class="list-tabs">
          <div
            :class="['tab-item', { active: rightTab === 'recipes' }]"
            @click="rightTab = 'recipes'"
          >
            <Icon type="ios-book" />
            丹方名录
          </div>
          <div
            :class="['tab-item', { active: rightTab === 'delegating' }]"
            @click="rightTab = 'delegating'"
          >
            <Icon type="ios-flame" />
            委托记录
            <Badge v-if="delegatingCount > 0" :count="delegatingCount" class="tab-badge" />
          </div>
        </div>
        <div v-show="rightTab === 'recipes'" class="tab-content">
          <div class="list-header">
            <div class="list-filter">
              <Input
                v-model="recipeSearch"
                placeholder="搜索丹方..."
                size="small"
                clearable
                class="search-input"
              >
                <Icon type="ios-search" slot="prefix" />
              </Input>
              <Select v-model="recipeFilter" size="small" class="filter-select">
                <Option value="all">全部</Option>
                <Option value="can">可炼制</Option>
                <Option value="hp">回血</Option>
                <Option value="mp">回灵</Option>
                <Option value="buff">增益</Option>
              </Select>
            </div>
          </div>

          <div class="recipe-items">
            <div
              v-for="(recipe, index) in filteredRecipes"
              :key="getRecipeKey(recipe, index)"
              :class="['recipe-item', {
                'active': isRecipeSelected(recipe),
                'can-refine': recipe.isMeet,
                'proficiency-max': recipe.proficiencyLevel >= recipe.maxProficiencyLevel
              }]"
              @click="selectRecipe(recipe)"
            >
              <!-- 丹方头部 -->
              <div class="recipe-header">
                <div class="recipe-basic">
                  <div class="recipe-name">{{ recipe.itemName }}</div>
<!--                  <div class="recipe-grade">
                    <Tag :color="getGradeColor(recipe.grade)" size="small">
                      {{ recipe.gradeName }}
                    </Tag>
                  </div>-->
                </div>
              </div>

              <!-- 熟练度条 -->
              <div class="recipe-proficiency">
                <div class="proficiency-header">
                  <span class="proficiency-label">熟练度</span>
                  <span class="proficiency-level">Lv.{{ recipe.proficiencyLevel }}</span>
                </div>
                <Progress
                  :percent="(recipe.currentProficiency / recipe.maxProficiency * 100)"
                  :stroke-width="4"
                  hide-info
                  :status="recipe.proficiencyLevel >= recipe.maxProficiencyLevel ? 'success' : 'normal'"
                />
              </div>

              <!-- 快捷信息 -->
              <div class="recipe-quick-info">
                <span class="quick-item" :title="'成功率 ' + (getRecipeRate(recipe) * 100).toFixed(0) + '%'">
                  <Icon type="ios-pulse" />
                  {{ (getRecipeRate(recipe) * 100).toFixed(0) }}%
                </span>
                <span class="quick-item" :title="'耗时 ' + recipe.costTime + '秒'">
                  <Icon type="ios-time" />
                  {{ recipe.costTime }}s
                </span>
                <span class="quick-item material-indicator" :class="{ 'ready': recipe.isMeet }">
                  <Icon :type="recipe.isMeet ? 'ios-checkmark' : 'ios-close'" />
                  材料
                </span>
              </div>
            </div>

            <div class="recipe-empty" v-if="filteredRecipes.length === 0">
              <Icon type="ios-search" size="32" />
              <p>未找到符合条件的丹方</p>
            </div>
          </div>
        </div>
        <div v-show="rightTab === 'delegating'" class="tab-content delegating-content">
          <div class="delegating-items">
            <div v-for="(item, index) in delegatingList" :key="item.id"
                 class="delegating-item"
                 :class="{
                   'can-receive': isAlchemyReceivable(item),
                   'active': selectedDelegatingItem && selectedDelegatingItem.id === item.id
                 }"
                 @click="selectDelegatingItem(item, index)">
              <div class="delegating-header">
                <div class="delegating-name">{{ item.name }}</div>
                <div class="delegating-status">
                  <span v-if="isAlchemyReceivable(item)" class="status-tag waiting">待领取</span>
                  <span v-else-if="isAlchemyProcessing(item)" class="status-tag processing">炼制中</span>
                  <span v-else class="status-tag completed">已领取</span>
                </div>
              </div>

              <div class="delegating-info">
                <div class="info-row">
                  <span class="info-label">炼制结果:</span>
                  <span class="info-value" >
              {{ item.result || '炼丹中...' }}
            </span>
                </div>
                <div class="info-row">
                  <span class="info-label">完成时间:</span>
                  <span class="info-value">{{ item.linTime }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">成功率:</span>
                  <span class="info-value success-rate">{{ item.rate != null ? (item.rate * 100).toFixed(2) + '%' : '--' }}</span>
                </div>
                <div class="info-row" v-if="item.productCount > 0">
                  <span class="info-label">产出数量:</span>
                  <span class="info-value product-count">x{{ item.productCount }}</span>
                </div>
              </div>

              <div class="delegating-actions">
                <Button type="success" size="small"
                        v-if="isAlchemyReceivable(item)"
                        @click="receiveAlchemy(item, index)"
                        :loading="receivingIndex === index">
                  <Icon type="ios-checkmark-circle" size="14" />
                  领取丹药
                </Button>
                <span v-else-if="isAlchemyProcessing(item)" class="countdown-text">
            <Icon type="ios-time" size="14" />
            剩余 {{ getCountdown(item.linTime) }}
          </span>
              </div>

            </div>

            <div class="empty-state" v-if="delegatingList.length === 0">
              <Icon type="ios-flame-outline" size="32" />
              <p>暂无委托炼丹任务</p>
              <p class="empty-hint">开始炼丹后会出现在这里</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>

export default {
  name: 'alchemyFurnace',

  data() {
    return {
      // 弹窗控制
      alchemyVisible: false,
      formulaMatZiList: [],     // 提交用的材料列表（对齐锻造）
      rightTab: 'recipes', // 右侧面板标签：recipes/delegating
      delegatingList: [], // 委托炼丹列表
      delegatingCount: 0, // 可领取数量
      receivingIndex: null, // 正在领取的索引
      selectedDelegatingItem: null,
      selectedDelegatingIndex: -1,
      // 图纸列表数据
      recipeList: [], // 已学丹方列表
      recipeSearch: '',
      recipeFilter: 'all',

      // 选中图纸
      selectedRecipe: null, // 选中的丹方详情
      auxiliaryNums: [], // 辅材数量输入

      // 炼丹状态
      isRefining: false,
      refiningProgress: 0,
      refiningTimer: null,

      // 角色数据
      characterEnergy: 0,
      characterMaxEnergy: 100,

      gamFordrawingBo: {
        charaId: "",
        packItemIds: "",
        matList: [],
        type:2,
      },
    }
  },

  computed: {
    // 过滤后的图纸列表
    filteredRecipes() {
      let list = this.recipeList;

      // 搜索过滤
      if (this.recipeSearch) {
        const keyword = this.recipeSearch.toLowerCase();
        list = list.filter(r =>
          ((r.itemName || '').toLowerCase().includes(keyword)) ||
          ((r.effectDesc || '').toLowerCase().includes(keyword))
        );
      }

      // 类型过滤
      if (this.recipeFilter === 'can') {
        list = list.filter(r => r.isMeet);
      } else if (this.recipeFilter !== 'all') {
        list = list.filter(r => r.pillType === this.recipeFilter);
      }

      //  排序：可炼制的在前，然后按熟练度等级排序
      return [...list].sort((a, b) => {
        if (a.isMeet !== b.isMeet) return b.isMeet - a.isMeet;
        if (a.proficiencyLevel !== b.proficiencyLevel) {
          return b.proficiencyLevel - a.proficiencyLevel;
        }
        return b.currentProficiency - a.currentProficiency;
      });
    },

    proficiencyPercent() {
      if (!this.selectedRecipe) return 0;
      return (this.selectedRecipe.currentProficiency / this.selectedRecipe.maxProficiency * 100);
    },
    selectedDisplayRecipe() {
      if (this.selectedDelegatingItem) {
        return {
          itemName: this.selectedDelegatingItem.name || '炼丹委托',
          grade: 'orange',
          gradeName: this.isAlchemyReceivable(this.selectedDelegatingItem) ? '待回收' : '炼制中'
        }
      }
      return this.selectedRecipe
    },
    selectedDelegatingSuccessItems() {
      return this.selectedDelegatingItem && Array.isArray(this.selectedDelegatingItem.successItems)
        ? this.selectedDelegatingItem.successItems
        : []
    },
    selectedDelegatingFailItems() {
      return this.selectedDelegatingItem && Array.isArray(this.selectedDelegatingItem.failItems)
        ? this.selectedDelegatingItem.failItems
        : []
    },

    canRefine() {
      return this.selectedRecipe && this.selectedRecipe.isMeet && !this.isRefining;
    },
    showRateDetail() {
      return !!(this.selectedRecipe && (
        Number(this.selectedRecipe.slotBonus || 0) > 0 ||
        Number(this.selectedRecipe.caiRate || 0) > 0 ||
        Number(this.selectedRecipe.proficiencyBonus || 0) > 0
      ));
    },

    // 不能炼丹的原因
    cannotRefineReason() {
      if (!this.selectedRecipe) return '请选择丹方';
      if (!this.selectedRecipe.isMeet) return '材料不足';
     // if (this.characterEnergy < this.selectedRecipe.costEnergy) return '鐏垫皵涓嶈冻';
      return '';
    },

    // 是否有足够灵气
    /*hasEnoughEnergy() {
      if (!this.selectedRecipe) return true;
      return this.characterEnergy >= this.selectedRecipe.costEnergy;
    }*/
  },

  methods: {
    isRequestSuccess(res) {
      return !!(res && res.data && Number(res.data.status) === 200);
    },
    getResponseMessage(res, fallback) {
      return (res && res.data && res.data.msg) || fallback;
    },
    notifyUserActivityRefresh() {
      this.$emit('user-activity-refresh');
    },
    // 打开炼丹炉
    openAlchemy() {
      this.alchemyVisible = true;
      this.loadRecipeList();
      this.loadDelegatingList(); // 加载委托记录
      //this.loadCharacterData();
    },

    // 加载丹方列表
    async loadRecipeList() {
      const charaId = this.getCookie("charaId");
      if (!charaId) return;
      try {
        const res = await this.$http.post(
          `/gameCharaEquip/queryLearnedFormulaList?charaId=${charaId}&formulaType=2`
        );
        if (!this.isRequestSuccess(res)) {
          this.$Message.warning(this.getResponseMessage(res, "加载丹方列表失败"));
          this.recipeList = [];
          return;
        }
        const baseList = (res && res.data && Array.isArray(res.data.data)) ? res.data.data : [];
        this.recipeList = baseList.map(this.normalizeRecipe);
      } catch (e) {
        this.$Message.warning((e && e.response && e.response.data && e.response.data.msg) || "加载丹方列表失败");
        this.recipeList = [];
      }
    },
    receiveAlchemy(item, index) {
      this.receivingIndex = index;

      this.$http
        .post(
          "/gameCharaEquip/receiveForgingPlayer?charaId=" +
          encodeURIComponent(this.getCookie("charaId")) +
          "&forPlayerId=" + encodeURIComponent(item.id)
        )
        .then((res) => {
          if (!this.isRequestSuccess(res)) {
            this.$Message.warning(this.getResponseMessage(res, "领取失败"));
            this.receivingIndex = null;
            return;
          }
          this.$Message.success((res && res.data && (res.data.data || res.data.msg)) || "领取成功");
          // 刷新列表
          this.loadDelegatingList();
          this.notifyUserActivityRefresh();
          this.receivingIndex = null;

          // 从列表中移除
          this.delegatingList.splice(index, 1);
        })
        .catch((err) => {
          this.$Message.warning((err && err.response && err.response.data && err.response.data.msg) || "领取失败");
          this.receivingIndex = null;
        });
    },
    loadDelegatingList() {
      this.$http
        .post(
          "/gameCharaEquip/queryForgingPlayer?charaId=" +
          this.getCookie("charaId") + "&formulaType=2"
        )
        .then((res) => {
          if (!this.isRequestSuccess(res)) {
            this.$Message.warning(this.getResponseMessage(res, "加载委托记录失败"));
            return;
          }
          const list = (res && res.data && Array.isArray(res.data.data)) ? res.data.data : [];
          this.delegatingList = [];
          this.delegatingCount = 0;
          for (let i = 0; i < list.length; i++) {
            if(list[i].receive == 0 ){
              this.delegatingList.push(list[i]);
              if (this.isAlchemyReceivable(list[i])) {
                this.delegatingCount++;
              }
            }else{
              //  this.tuzhiingList2.push(list[i]);
            }
          }
          if (this.selectedDelegatingItem) {
            const latest = this.delegatingList.find(v => v.id === this.selectedDelegatingItem.id)
            this.selectedDelegatingItem = latest || null
            this.selectedDelegatingIndex = latest ? this.delegatingList.findIndex(v => v.id === latest.id) : -1
          }
          // 启动倒计时?
          //this.startCountdowns();
        })
        .catch((err) => {
          this.$Message.warning((err && err.response && err.response.data && err.response.data.msg) || "加载委托记录失败");
        });

    },
    // 选择丹方
    selectRecipe(recipe) {
      this.selectedDelegatingItem = null
      this.selectedDelegatingIndex = -1
      this.selectedRecipe = this.normalizeRecipe(recipe);
      // 调用详情接口（复用锻造的 queryForgingOrPills）
      this.$http
        .post(
          "/gameCharaEquip/queryForgingOrPills?charaId=" +
          this.getCookie("charaId") +
          "&packItemIds=" + recipe.formulaId + "&formulaType=2"
        )
        .then((res) => {
          if (!this.isRequestSuccess(res)) {
            this.$Message.warning(this.getResponseMessage(res, "查询丹方详情失败"));
            return;
          }
          const data = (res && res.data && res.data.data) ? res.data.data : {};

          // 合并列表和详情，并做字段兜底，避免有数据却显示空
          this.selectedRecipe = this.normalizeRecipe({
            ...recipe,
            ...data,
            formulaId: recipe.formulaId,
            isMeet: typeof data.isMeet === 'boolean' ? data.isMeet : recipe.isMeet
          });

          // 初始化辅材数量（对齐锻造逻辑）
          this.initAuxiliaryMaterials();
        })
        .catch((err) => {
          this.$Message.warning((err && err.response && err.response.data && err.response.data.msg) || "查询丹方详情失败");
        });

    },
    selectDelegatingItem(item, index) {
      this.selectedDelegatingItem = item
      this.selectedDelegatingIndex = index
      this.selectedRecipe = null
    },

    /** 初始化辅材数量 - 完全对齐锻造 */
    initAuxiliaryMaterials() {
      this.auxiliaryNums = [];
      this.formulaMatZiList = [];

      if (!this.selectedRecipe || !this.selectedRecipe.matList) return;

      this.selectedRecipe.matList.forEach((mat, idx) => {
        if (this.isMainMaterial(mat)) {
          // 主材：固定数量
          this.auxiliaryNums[idx] = mat.num;
        } else {
          // 辅材：默认使用最大可用（对齐锻造）
          const defaultNum = Math.min(mat.haveNum, mat.num);
          this.auxiliaryNums[idx] = defaultNum;

          // 初始化提交列表（type=2是辅材成功率，type=4是辅材品质）
          if (String(mat.type) === '2' || String(mat.type) === '4') {
            this.formulaMatZiList.push({
              itemId: mat.itemId,
              haveNum: defaultNum,  // 对齐锻造的字段名
              type: mat.type
            });
          }
        }
      });

      this.updateMaterialStatus();
    },

    /** 辅材数量变化 - 对齐锻造的 getmatList */
    onAuxiliaryChange(itemId, value, index) {
      // 更新输入值
      this.$set(this.auxiliaryNums, index, value);

      // 更新提交列表（对齐锻造逻辑）
      const mat = this.selectedRecipe.matList[index];
      let found = false;

      for (let i = 0; i < this.formulaMatZiList.length; i++) {
        if (this.formulaMatZiList[i].itemId === itemId) {
          this.formulaMatZiList[i].haveNum = value;  // 对齐锻盖的字段
          found = true;
          break;
        }
      }

      if (!found && value > 0) {
        this.formulaMatZiList.push({
          itemId: itemId,
          haveNum: value,
          type: mat.type
        });
      }

      this.updateMaterialStatus();
    },

    /** 更新材料满足状态 */
    updateMaterialStatus() {
      if (!this.selectedRecipe) return;
      const isMeet = this.computeRecipeIsMeet(this.selectedRecipe, this.auxiliaryNums, 1);

      // 更新选中项的状态（响应式）
      this.$set(this.selectedRecipe, 'isMeet', isMeet);

      // 同时更新列表中的状态
      const idx = this.recipeList.findIndex(r => r.formulaId === this.selectedRecipe.formulaId);
      if (idx !== -1) {
        this.$set(this.recipeList[idx], 'isMeet', isMeet);
      }
    },

    /** 开始炼丹 - 对齐锻造的 duanzao */
    async startRefining() {
      this.updateMaterialStatus();
      if (!this.canRefine) return;

      this.isRefining = true;
      this.refiningProgress = 0;

      // 进度条动画
      const duration = this.selectedRecipe.costTime * 1000;
      const step = 100 / (duration / 100);
      this.refiningTimer = setInterval(() => {
        this.refiningProgress = Math.min(100, this.refiningProgress + step);
      }, 100);

      try {
        // 调用后台接口（复用锻造的 forgingEquipmentOrPills）
        const res = await this.$http.post('/gameCharaEquip/forgingEquipmentOrPills', {
          ...this.gamFordrawingBo,
          charaId: this.getCookie("charaId"),
          packItemIds: this.selectedRecipe.formulaId,
          matList: this.formulaMatZiList,
          type: 2,
          batchCount: this.selectedRecipe && this.selectedRecipe.batchMax ? this.selectedRecipe.batchMax : 12,
        });
        const body = (res && res.data) ? res.data : {}
        const businessOk = Number(body.status) === 200

        if (!businessOk) {
          throw new Error(body.msg || '炼丹请求失败')
        }
        const resultText = body.data || body.msg || '炼丹委托成功！'

        // 完成动画
        clearInterval(this.refiningTimer);
        this.refiningProgress = 100;

        setTimeout(async () => {
          this.isRefining = false;
          this.refiningProgress = 0;

          // 显示结果（后台返回的数据）
          this.showRefineResult(resultText);
          this.notifyUserActivityRefresh();

          // 刷新数据
          await this.loadRecipeList();      // 刷新列表
          if (this.selectedRecipe && this.selectedRecipe.formulaId) {
            const latest = this.recipeList.find(r => r.formulaId === this.selectedRecipe.formulaId);
            if (latest) this.selectRecipe(latest);
          }

        }, 500);

      } catch (err) {
        clearInterval(this.refiningTimer);
        this.isRefining = false;
        this.refiningProgress = 0;
        this.$Notice.error({
          title: '炼丹结果',
          desc:
            (err && err.message) ||
            (err && err.response && err.response.data && err.response.data.msg) ||
            '未知错误'
        });
      }
    },

    /** 显示结果 - 简化版 */
    showRefineResult(data) {
      const text = (data || '').toString();
      const isSuccess = text.includes('成功') || text.includes('大成功');
      const isCrit = text.includes('大成功');

      this.$Modal.success({
        title: isSuccess ? '炼丹开始' : '炼丹开始',
        content: `
          <div style="text-align: center; padding: 20px;">
            <Icon type="ios-${isSuccess ? 'checkmark-circle' : 'close-circle'}"
                  size="64"
                  color="${isSuccess ? '#19be6b' : '#ed4014'}" />
            <h3 style="margin: 15px 0;">${text}</h3>
            ${isCrit ? '<p style="color: #ff9900;">暴击！产出翻倍</p>' : ''}
          </div>
        `,
        okText: '确定'
      });
    },
    // 模态框可见性变化
    handleAlchemyModalVisible(visible) {
      if (!visible) {
        // 清理
        clearInterval(this.refiningTimer);
        this.isRefining = false;
        this.selectedRecipe = null;
        this.selectedDelegatingItem = null;
        this.selectedDelegatingIndex = -1;
      }
    },

    // 获取品质颜色
    getGradeColor(grade) {
      const colors = {
        'white': 'default',
        'green': 'success',
        'blue': 'primary',
        'purple': 'warning',
        'orange': 'error',
        'red': '#ff4d4f'
      };
      return colors[grade] || 'default';
    },

    // 获取成功率样式类
    getRateClass(rate) {
      if (rate >= 0.8) return 'high';
      if (rate >= 0.5) return 'medium';
      return 'low';
    },
    getRecipeRate(recipe) {
      const baseRaw = (recipe.baseRate !== undefined && recipe.baseRate !== null)
        ? recipe.baseRate
        : ((recipe.basicRate !== undefined && recipe.basicRate !== null) ? recipe.basicRate : 0);
      const profBonusRaw = (recipe.proficiencyBonus !== undefined && recipe.proficiencyBonus !== null)
        ? recipe.proficiencyBonus
        : 0;
      const slotBonusRaw = (recipe.slotBonus !== undefined && recipe.slotBonus !== null)
        ? recipe.slotBonus
        : 0;
      const caiRateRaw = (recipe.caiRate !== undefined && recipe.caiRate !== null)
        ? recipe.caiRate
        : 0;
      const base = Number(baseRaw);
      const profBonus = Number(profBonusRaw);
      const slotBonus = Number(slotBonusRaw);
      const caiRate = Number(caiRateRaw);
      return Math.max(0, Math.min(1, base + slotBonus + caiRate + profBonus));
    },
    isMainMaterial(mat) {
      return String(mat && mat.type) === '1';
    },
    toNum(val) {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    },
    computeRecipeIsMeet(recipe, auxiliaryNums = [], batchCount = 1) {
      if (!recipe || !Array.isArray(recipe.matList) || recipe.matList.length === 0) {
        return false;
      }

      for (let i = 0; i < recipe.matList.length; i++) {
        const mat = recipe.matList[i] || {};
        const haveNum = this.toNum(mat.haveNum);
        const requiredTotal = this.getRequiredTotalByInputs(mat, i, auxiliaryNums, batchCount);

        if (haveNum < requiredTotal) {
          return false;
        }
      }

      return true;
    },
    getRequiredTotalByInputs(mat, index, auxiliaryNums = [], batchCount = 1) {
      const baseNeedPerBatch = Math.max(this.toNum(mat && mat.num), 0);
      const selectedAuxPerBatch = this.isMainMaterial(mat)
        ? baseNeedPerBatch
        : Math.max(this.toNum(auxiliaryNums[index]), 0);
      const requiredPerBatch = Math.max(baseNeedPerBatch, selectedAuxPerBatch);
      return requiredPerBatch * Math.max(this.toNum(batchCount), 1);
    },
    getRequiredTotal(mat, index) {
      return this.getRequiredTotalByInputs(mat, index, this.auxiliaryNums, 1);
    },
    isMaterialEnough(mat, index) {
      return this.toNum(mat && mat.haveNum) >= this.getRequiredTotal(mat, index);
    },
    getRecipeKey(recipe, index) {
      if (!recipe) return `recipe-${index}`;
      return recipe.formulaId || recipe.packItemId || recipe.recipeId || recipe.itemId || recipe.id || `recipe-${index}`;
    },
    isRecipeSelected(recipe) {
      if (!this.selectedRecipe || !recipe) return false;
      return this.getRecipeKey(this.selectedRecipe, -1) === this.getRecipeKey(recipe, -1);
    },
    normalizeRecipe(raw) {
      const recipe = { ...(raw || {}) };
      const matList = Array.isArray(recipe.matList) ? recipe.matList : [];
      recipe.formulaId = recipe.formulaId || recipe.id || recipe.gfFormulaId || '';
      recipe.itemName = recipe.itemName || recipe.name || '未命名丹方';
      recipe.productName = recipe.productName || recipe.itemName || '-';
      recipe.desci = recipe.desci || recipe.effectDesc || '';
      recipe.costTime = Number(recipe.costTime || 0);
      recipe.currentProficiency = Number(recipe.currentProficiency || 0);
      recipe.maxProficiency = Number(recipe.maxProficiency || 1);
      recipe.proficiencyLevel = Number(recipe.proficiencyLevel || 0);
      recipe.proficiencyBonus = Number(recipe.proficiencyBonus || 0);
      recipe.slotBonus = Number(recipe.slotBonus || 0);
      recipe.caiRate = Number(recipe.caiRate || 0);
      const basicRateRaw = (recipe.basicRate !== undefined && recipe.basicRate !== null)
        ? recipe.basicRate
        : ((recipe.baseRate !== undefined && recipe.baseRate !== null) ? recipe.baseRate : 0);
      const baseRateRaw = (recipe.baseRate !== undefined && recipe.baseRate !== null)
        ? recipe.baseRate
        : ((recipe.basicRate !== undefined && recipe.basicRate !== null) ? recipe.basicRate : 0);
      recipe.basicRate = Number(basicRateRaw);
      recipe.baseRate = Number(baseRateRaw);
      recipe.batchMax = Number(recipe.batchMax || 12);
      recipe.pillType = recipe.pillType || '';
      const serverIsMeet = (typeof recipe.isMeet === 'boolean') ? recipe.isMeet : null;
      recipe.matList = matList.map((m) => ({
        ...m,
        type: String(m.type),
        num: Number(m.num || 0),
        haveNum: Number(m.haveNum || 0)
      }));
      const strictMeet = this.computeRecipeIsMeet(recipe, [], 1);
      recipe.isMeet = (serverIsMeet === null) ? strictMeet : (serverIsMeet && strictMeet);
      return recipe;
    },
    getCountdown(endTime) {
      if (!endTime) return '--';
      const end = new Date(endTime).getTime();
      if (Number.isNaN(end)) return '--';
      const diff = end - Date.now();
      if (diff <= 0) return '00:00';
      const total = Math.floor(diff / 1000);
      const mm = String(Math.floor(total / 60)).padStart(2, '0');
      const ss = String(total % 60).padStart(2, '0');
      return `${mm}:${ss}`;
    },
    isAlchemyReceivable(item) {
      if (!item || item.receive !== 0) return false
      const end = new Date(item.linTime).getTime()
      if (Number.isNaN(end)) return false
      return end <= Date.now()
    },
    isAlchemyProcessing(item) {
      if (!item || item.receive !== 0) return false
      return !this.isAlchemyReceivable(item)
    },

    getCookie(name) {
      // 你的cookie获取方法
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    }
  }
}
</script>

<style scoped>
.alchemy-layout {
  display: flex;
  height: 750px;
}

.alchemy-furnace-area {
  width: 70%;
  display: flex;
  flex-direction: column;
  color: #1a1a2e;
  overflow: auto;
}

.alchemy-furnace-area .furnace-visual {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.alchemy-furnace-area .furnace-visual.refining {
  background: radial-gradient(circle at center, rgba(255,107,107,0.2) 0%, transparent 70%);
}

.alchemy-furnace-area .furnace-visual .furnace-container {
  position: relative;
  text-align: center;
}

.alchemy-furnace-area .furnace-visual .furnace-img {
  width: 200px;
  height: 200px;
  object-fit: contain;
  filter: drop-shadow(0 0 20px rgba(255,107,107,0.5));
  animation: furnace-float 3s ease-in-out infinite;
}

@keyframes furnace-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.alchemy-furnace-area .furnace-visual .refining-animation .flame-effect {
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 150px;
  background: radial-gradient(ellipse at center, rgba(255,107,107,0.8) 0%, transparent 70%);
  animation: flame-pulse 0.5s ease-in-out infinite;
}

@keyframes flame-pulse {
  0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.2); }
}

.alchemy-furnace-area .furnace-visual .refining-animation .smoke-effect {
  position: absolute;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, rgba(200,200,200,0.3) 0%, transparent 70%);
  animation: smoke-rise 2s ease-out infinite;
}

@keyframes smoke-rise {
  0% { opacity: 0; transform: translateX(-50%) translateY(0) scale(0.5); }
  50% { opacity: 0.5; }
  100% { opacity: 0; transform: translateX(-50%) translateY(-50px) scale(1.5); }
}

.alchemy-furnace-area .furnace-visual .refining-animation .flame-icon {
  animation: icon-shake 0.3s ease-in-out infinite;
}

@keyframes icon-shake {
  0%, 100% { transform: rotate(-5deg); }
  50% { transform: rotate(5deg); }
}

.alchemy-furnace-area .furnace-visual .refining-animation .refining-text {
  margin-top: 15px;
  font-size: 18px;
  color: #ff6b6b;
  text-shadow: 0 0 10px rgba(255,107,107,0.5);
}

.alchemy-furnace-area .furnace-visual .refining-animation .refining-progress {
  margin-top: 20px;
  width: 300px;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float {
  position: absolute;
  top: 0px;
  right: 20px;
  background: rgba(0,0,0,0.6);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  backdrop-filter: blur(10px);
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-preview-img {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info {
  text-align: left;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-name {
  font-size: 16px;
  font-weight: bold;
  color: #fff;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade {
  font-size: 12px;
  margin-top: 4px;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade.white {
  color: #ccc;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade.green {
  color: #19be6b;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade.blue {
  color: #2db7f5;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade.purple {
  color: #a855f7;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade.orange {
  color: #ff9900;
}

.alchemy-furnace-area .furnace-visual .selected-pill-float .pill-float-info .pill-grade.red {
  color: #ff4d4f;
}

.alchemy-furnace-area .materials-section {
  padding: 15px 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.alchemy-furnace-area .materials-section .section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #19be6b;
  margin-bottom: 12px;
}

.alchemy-furnace-area .materials-section .section-title .material-status {
  margin-left: auto;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(237,64,20,0.2);
  color: #ed4014;
}

.alchemy-furnace-area .materials-section .section-title .material-status.ready {
  background: rgba(25,190,107,0.2);
  color: #19be6b;
}

.alchemy-furnace-area .materials-section .section-title .material-status i {
  margin-right: 4px;
}

.alchemy-furnace-area .materials-section .materials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.alchemy-furnace-area .materials-section .material-slot {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  transition: all 0.3s;
}

.alchemy-furnace-area .materials-section .material-slot.sufficient {
  border-color: rgba(25,190,107,0.3);
  background: rgba(25,190,107,0.05);
}

.alchemy-furnace-area .materials-section .material-slot.insufficient {
  border-color: rgba(237,64,20,0.3);
  opacity: 0.7;
}

.alchemy-furnace-area .materials-section .material-slot.main-material {
  border-width: 2px;
  border-color: rgba(255,107,107,0.5);
}

.alchemy-furnace-area .materials-section .material-slot .mat-icon {
  position: relative;
  width: 70px;
  height: 40px;
  margin: 0 auto 8px;
}

.alchemy-furnace-area .materials-section .material-slot .mat-icon img {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  object-fit: cover;
}

.alchemy-furnace-area .materials-section .material-slot .mat-icon .mat-quantity-badge {
  position: absolute;
  bottom: -5px;
  right: 20px;
  background: #2d3436;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.2);
}

.sufficient .alchemy-furnace-area .materials-section .material-slot .mat-icon .mat-quantity-badge {
  background: #19be6b;
}

.insufficient .alchemy-furnace-area .materials-section .material-slot .mat-icon .mat-quantity-badge {
  background: #ed4014;
}

.alchemy-furnace-area .materials-section .material-slot .mat-info .mat-name {
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alchemy-furnace-area .materials-section .material-slot .mat-info .mat-type {
  font-size: 10px;
  margin-top: 2px;
}

.alchemy-furnace-area .materials-section .material-slot .mat-adjust {
  margin-top: 8px;
}

.alchemy-furnace-area .materials-section .material-slot .mat-adjust /deep/ .ivu-input-number {
  width: 80px;
}

.alchemy-furnace-area .materials-section .material-slot .mat-adjust /deep/ .ivu-input-number input {
  background: rgba(0,0,0,0.3);
  color: #fff;
}

.alchemy-furnace-area .alchemy-info-section {
  padding: 15px 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.alchemy-furnace-area .alchemy-info-section .info-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 15px;
}

.alchemy-furnace-area .alchemy-info-section .info-block {
  flex: 1;
  background: rgba(255,255,255,0.05);
  border-radius: 8px;
  padding: 12px;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-label i {
  font-size: 14px;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-number {
  font-size: 24px;
  font-weight: bold;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-number.high {
  color: #19be6b;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-number.medium {
  color: #ff9900;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-number.low {
  color: #ed4014;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-detail {
  font-size: 11px;
  color: #999;
  margin-top: 4px;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-detail .plus {
  margin: 0 4px;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .rate-detail .bonus {
  color: #19be6b;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .proficiency-level {
  font-size: 18px;
  font-weight: bold;
  color: #2db7f5;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .proficiency-bar {
  margin: 8px 0;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .proficiency-text {
  font-size: 11px;
  color: #999;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .cost-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #fff;
  margin-bottom: 4px;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .cost-item .insufficient {
  color: #ed4014;
}

.alchemy-furnace-area .alchemy-info-section .info-block .block-value .cost-item i {
  color: #999;
}

.alchemy-furnace-area .alchemy-info-section .products-preview {
  display: flex;
  gap: 15px;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box {
  flex: 1;
  background: rgba(255,255,255,0.05);
  border-radius: 8px;
  padding: 8px;
  border-left: 3px solid;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box.success-product {
  border-left-color: #19be6b;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box.fail-product {
  border-left-color: #ed4014;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box .product-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box .product-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box .product-item .product-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box .product-item .product-name {
  flex: 1;
  font-size: 13px;
}

.alchemy-furnace-area .alchemy-info-section .products-preview .product-box .product-item .product-count {
  font-size: 14px;
  font-weight: bold;
  color: #ff9900;
}

.alchemy-furnace-area .alchemy-action {
  padding: 15px 20px;
  border-top: 1px solid rgba(255,255,255,0.1);
  text-align: center;
}

.alchemy-furnace-area .alchemy-action .refine-btn {
  width: 200px;
  height: 44px;
  font-size: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.alchemy-furnace-area .alchemy-action .refine-btn.can-refine {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  box-shadow: 0 4px 15px rgba(255,107,107,0.4);
}

.alchemy-furnace-area .alchemy-action .refine-btn.can-refine:hover {
  box-shadow: 0 6px 20px rgba(255,107,107,0.6);
  transform: translateY(-2px);
}

.alchemy-furnace-area .alchemy-action .refine-btn:disabled {
  background: #2d3436;
  color: #636e72;
}

.alchemy-furnace-area .alchemy-action .refine-btn i {
  margin-right: 8px;
}

.alchemy-furnace-area .alchemy-action .cannot-refine-reason {
  margin-top: 10px;
  font-size: 12px;
  color: #ed4014;
}

.alchemy-furnace-area .alchemy-info-section .info-block.action-inline-block .action-inline-content {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
}

.alchemy-furnace-area .alchemy-info-section .info-block.action-inline-block .batch-tip {
  margin-bottom: 8px;
  font-size: 12px;
  color: #999;
}

.alchemy-furnace-area .alchemy-info-section .info-block.action-inline-block .refine-btn {
  width: 100%;
  max-width: 220px;
  align-self: center;
  height: 40px;
  font-size: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.alchemy-furnace-area .alchemy-info-section .info-block.action-inline-block .refine-btn.can-refine {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  box-shadow: 0 4px 15px rgba(255,107,107,0.4);
}

.alchemy-furnace-area .alchemy-info-section .info-block.action-inline-block .refine-btn.can-refine:hover {
  box-shadow: 0 6px 20px rgba(255,107,107,0.6);
  transform: translateY(-1px);
}

.alchemy-furnace-area .alchemy-info-section .info-block.action-inline-block .cannot-refine-reason {
  margin-top: 8px;
  font-size: 12px;
  color: #ed4014;
  text-align: center;
}

.alchemy-furnace-area .no-recipe-tip {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #636e72;
}

.alchemy-furnace-area .no-recipe-tip p {
  margin-top: 15px;
  font-size: 14px;
}

.alchemy-recipe-list {
  width: 30%;
  background: #f8f9fa;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.alchemy-recipe-list .list-header {
  padding: 15px;
  border-bottom: 1px solid #e8e8e8;
}

.alchemy-recipe-list .list-header .list-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alchemy-recipe-list .list-header .list-title i {
  color: #2db7f5;
}

.alchemy-recipe-list .list-header .list-filter {
  display: flex;
  gap: 8px;
}

.alchemy-recipe-list .list-header .list-filter .search-input {
  flex: 1;
}

.alchemy-recipe-list .list-header .list-filter .filter-select {
  width: 90px;
}

.alchemy-recipe-list .recipe-items {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.alchemy-recipe-list .recipe-items .recipe-item {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.alchemy-recipe-list .recipe-items .recipe-item:hover {
  border-color: #2db7f5;
  box-shadow: 0 2px 8px rgba(45,183,245,0.1);
}

.alchemy-recipe-list .recipe-items .recipe-item.active {
  border-color: #2db7f5;
  background: rgba(45,183,245,0.05);
}

.alchemy-recipe-list .recipe-items .recipe-item.can-refine {
  border-left: 3px solid #19be6b;
}

.alchemy-recipe-list .recipe-items .recipe-item.proficiency-max .recipe-proficiency {
  opacity: 0.6;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-header {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-header .recipe-icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-header .recipe-basic {
  flex: 1;
  min-width: 0;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-header .recipe-basic .recipe-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-header .recipe-basic .recipe-grade {
  margin-top: 4px;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-proficiency {
  margin-bottom: 10px;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-proficiency .proficiency-header {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
  margin-bottom: 4px;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-proficiency .proficiency-header .proficiency-level {
  color: #2db7f5;
  font-weight: 500;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-quick-info {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #999;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-quick-info .quick-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-quick-info .quick-item i {
  font-size: 12px;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-quick-info .quick-item.material-indicator.ready {
  color: #19be6b;
}

.alchemy-recipe-list .recipe-items .recipe-item .recipe-quick-info .quick-item.material-indicator:not(.ready) {
  color: #ed4014;
}

.alchemy-recipe-list .recipe-items .recipe-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.alchemy-recipe-list .recipe-items .recipe-empty p {
  margin-top: 10px;
}

@media (max-width: 768px) {
  .alchemy-layout {
    flex-direction: column;
    height: auto;
    min-height: 500px;
  }

  .alchemy-furnace-area,
  .alchemy-recipe-list {
    width: 100%;
  }

  .alchemy-recipe-list {
    border-left: none;
    border-top: 1px solid #e8e8e8;
    max-height: 300px;
  }

  .alchemy-furnace-area .alchemy-info-section .info-row {
    grid-template-columns: 1fr;
  }
}

.description-section {
  padding: 15px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.description-section .section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 12px;
}

.description-section .section-title i {
  color: #2db7f5;
}

.description-section .description-content {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 15px;
  font-size: 13px;
  color: #716f6f;
  line-height: 1.6;
  min-height: 60px;
  max-height: 100px;
  overflow-y: auto;
}

.description-section .description-content::-webkit-scrollbar {
  width: 4px;
}

.description-section .description-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.description-section .description-content:empty::before {
  content: '鏆傛棤鎻忚堪';
  color: #666;
  font-style: italic;
}

.list-tabs {
  display: flex;
  border-bottom: 1px solid #e8e8e8;
}

.list-tabs .tab-item {
  flex: 1;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
}

.list-tabs .tab-item:hover {
  color: #2db7f5;
  background: rgba(45, 183, 245, 0.05);
}

.list-tabs .tab-item.active {
  color: #2db7f5;
  font-weight: 500;
}

.list-tabs .tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 20%;
  right: 20%;
  height: 2px;
  background: #2db7f5;
}

.list-tabs .tab-item .tab-badge {
  position: absolute;
  top: 8px;
  right: 15%;
}

.tab-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.delegating-content {
  background: #f8f9fa;
}

.delegating-items {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.delegating-item {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  transition: all 0.3s;
}

.delegating-item:hover {
  border-color: #2db7f5;
  box-shadow: 0 2px 8px rgba(45, 183, 245, 0.1);
}

.delegating-item.active {
  border-color: #2db7f5;
  box-shadow: 0 0 0 1px rgba(45, 183, 245, 0.18);
  background: rgba(45, 183, 245, 0.04);
}

.delegating-item.can-receive {
  border-left: 3px solid #19be6b;
  background: rgba(25, 190, 107, 0.02);
}

.delegating-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.delegating-header .delegating-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.delegating-header .status-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 4px;
}

.delegating-header .status-tag.waiting {
  background: rgba(25, 190, 107, 0.1);
  color: #19be6b;
}

.delegating-header .status-tag.processing {
  background: rgba(255, 153, 0, 0.1);
  color: #ff9900;
}

.delegating-header .status-tag.completed {
  background: #f0f0f0;
  color: #999;
}

.delegating-info {
  margin-bottom: 12px;
}

.delegating-info .info-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.delegating-info .info-row .info-label {
  color: #999;
  width: 70px;
}

.delegating-info .info-row .info-value {
  color: #333;
  flex: 1;
}

.delegating-info .info-row .info-value.text-success {
  color: #19be6b;
  font-weight: 500;
}

.delegating-info .info-row .info-value.text-fail {
  color: #ed4014;
}

.delegating-info .info-row .info-value.success-rate {
  color: #2db7f5;
}

.delegating-info .info-row .info-value.product-count {
  color: #ff9900;
  font-weight: bold;
}

.delegating-info .info-row .crit-tag {
  margin-left: 8px;
  font-size: 11px;
  color: #ff9900;
  background: rgba(255, 153, 0, 0.1);
  padding: 1px 6px;
  border-radius: 4px;
}

.delegating-result-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.delegating-summary-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #555;
}

.delegating-result-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.delegating-result-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

.delegating-result-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.delegating-result-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.4;
}

.delegating-result-chip.success {
  background: rgba(25, 190, 107, 0.12);
  color: #19be6b;
}

.delegating-result-chip.fail {
  background: rgba(237, 64, 20, 0.12);
  color: #ed4014;
}

.delegating-result-empty {
  font-size: 12px;
  color: #999;
}

.delegating-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.delegating-actions .countdown-text {
  font-size: 12px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-state p {
  margin-top: 10px;
  font-size: 14px;
}

.empty-state .empty-hint {
  font-size: 12px;
  color: #bbb;
  margin-top: 5px;
}
</style>




// WEBPACK FOOTER //
// src/components/alchemyFurnace.vue